import { formatPrice } from "./format";

const DRAG_MIME = "application/x-mrpanda-cart-item";

export type FlyItem = {
  productId: string;
  psId: number | null;
  name: string;
  price: number;
  imageUrl: string | null;
  originalPrice?: number | null;
  stock?: number | null;
};

export const CART_DROP_TARGET_ID = "mrpanda-cart-drop-target";
export const CART_DROP_TARGET_MOBILE_ID = "mrpanda-cart-drop-target-mobile";
export const CART_DRAG_MIME = DRAG_MIME;

export function serializeDrag(item: FlyItem) {
  return JSON.stringify(item);
}

export function parseDrag(raw: string): FlyItem | null {
  try {
    return JSON.parse(raw) as FlyItem;
  } catch {
    return null;
  }
}

/** Build a playful drag ghost card that clearly says "I'm being dragged". */
function createPlayfulDragGhost(
  imageUrl: string | null | undefined,
  name: string | null | undefined,
  price: number,
  sourceImg?: HTMLImageElement | null,
): HTMLElement {
  const root = document.createElement("div");
  // ΣΗΜΑΝΤΙΚΟ: το snapshot του drag preview κόβεται από τον browser όταν το
  // στοιχείο βρίσκεται εκτός viewport. Το τοποθετούμε λοιπόν μέσα στο viewport
  // αλλά πίσω από τη σελίδα (z-index -1), ώστε να μη φαίνεται ποτέ.
  root.style.position = "fixed";
  root.style.left = "0px";
  root.style.top = "0px";
  root.style.zIndex = "-1";
  root.style.boxSizing = "border-box";
  root.style.width = "148px";
  root.style.padding = "8px";
  root.style.background = "var(--card, #fff)";
  root.style.borderRadius = "14px";
  root.style.boxShadow = "0 14px 34px rgba(0,0,0,0.22)";
  root.style.border = "2px dashed var(--primary, #c83c00)";
  root.style.display = "flex";
  root.style.flexDirection = "column";
  root.style.gap = "6px";
  root.style.pointerEvents = "none";
  root.style.fontFamily = "var(--font-sans, system-ui)";

  const imgBox = document.createElement("div");
  imgBox.style.position = "relative";
  imgBox.style.width = "100%";
  imgBox.style.aspectRatio = "1";
  imgBox.style.background = "#fff";
  imgBox.style.borderRadius = "10px";
  imgBox.style.overflow = "hidden";
  imgBox.style.display = "grid";
  imgBox.style.placeItems = "center";

  const resolvedUrl = sourceImg?.currentSrc || sourceImg?.src || imageUrl || null;
  const sourceReady = !!sourceImg && sourceImg.complete && sourceImg.naturalWidth > 0;

  const showPanda = () => {
    imgBox.textContent = "🐼";
    imgBox.style.fontSize = "42px";
    imgBox.style.animation = "mp-drop-arrow .9s ease-in-out infinite";
  };

  const makeImg = (src: string) => {
    const img = document.createElement("img");
    img.src = src;
    img.alt = "";
    img.style.width = "84%";
    img.style.height = "84%";
    img.style.objectFit = "contain";
    return img;
  };

  if (sourceReady && resolvedUrl) {
    // Η εικόνα είναι ήδη φορτωμένη: το snapshot του drag preview γίνεται
    // σύγχρονα, οπότε τη βάζουμε κατευθείαν χωρίς αναμονή δικτύου.
    imgBox.appendChild(makeImg(resolvedUrl));
  } else if (resolvedUrl) {
    // Δεν έχει φορτώσει ακόμη: δείχνουμε παιχνιδιάρικο panda και την
    // αντικαθιστούμε μόλις είναι έτοιμη (για το ghost που μένει στην οθόνη).
    showPanda();
    const pending = new Image();
    pending.decoding = "sync";
    pending.src = resolvedUrl;
    const swap = () => {
      if (!root.isConnected) return;
      imgBox.textContent = "";
      imgBox.style.fontSize = "";
      imgBox.style.animation = "";
      imgBox.appendChild(makeImg(resolvedUrl));
    };
    if (pending.complete && pending.naturalWidth > 0) swap();
    else pending.addEventListener("load", swap, { once: true });
  } else {
    showPanda();
  }

  const title = document.createElement("div");
  title.textContent = (name || "Προϊόν").slice(0, 38);
  title.style.fontSize = "11px";
  title.style.fontWeight = "600";
  title.style.lineHeight = "1.25";
  title.style.maxHeight = "2.5em";
  title.style.overflow = "hidden";
  title.style.color = "var(--foreground, #1f2937)";

  const row = document.createElement("div");
  row.style.display = "flex";
  row.style.alignItems = "center";
  row.style.justifyContent = "space-between";

  const priceTag = document.createElement("span");
  priceTag.textContent = formatPrice(price);
  priceTag.style.fontSize = "12px";
  priceTag.style.fontWeight = "700";
  priceTag.style.color = "var(--primary, #c83c00)";

  const panda = document.createElement("span");
  panda.textContent = "🐼";
  panda.style.fontSize = "18px";

  row.appendChild(priceTag);
  row.appendChild(panda);

  root.appendChild(imgBox);
  root.appendChild(title);
  root.appendChild(row);

  return root;
}

/** Show a small, clean drag preview that contains ONLY the product image
 * (no buttons, prices, badges or other symbols). Pass the product image URL;
 * falls back to the panda emoji when there is no image. Safe to call on SSR
 * (no-op without a document). */
export function setCleanProductDragImage(
  dataTransfer: DataTransfer | null,
  imageUrl: string | null | undefined,
  sourceImg?: HTMLImageElement | null,
) {
  if (typeof document === "undefined" || !dataTransfer) return;

  const resolvedUrl = sourceImg?.currentSrc || sourceImg?.src || imageUrl || null;

  if (sourceImg && sourceImg.complete && sourceImg.naturalWidth > 0) {
    const r = sourceImg.getBoundingClientRect();
    dataTransfer.setDragImage(sourceImg, r.width / 2, r.height / 2);
    return;
  }

  const ghost = document.createElement("div");
  ghost.style.position = "fixed";
  ghost.style.left = "0px";
  ghost.style.top = "0px";
  ghost.style.zIndex = "-1";
  ghost.style.width = "112px";
  ghost.style.height = "112px";
  ghost.style.pointerEvents = "none";
  ghost.style.display = "grid";
  ghost.style.placeItems = "center";
  ghost.style.background = "transparent";

  if (resolvedUrl) {
    const img = document.createElement("img");
    img.src = resolvedUrl;
    img.alt = "";
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.objectFit = "contain";
    img.style.opacity = "0.92";
    ghost.appendChild(img);
  } else {
    ghost.textContent = "🐼";
    ghost.style.fontSize = "48px";
  }

  document.body.appendChild(ghost);
  dataTransfer.setDragImage(ghost, 56, 56);
  window.setTimeout(() => ghost.remove(), 0);
}

/** Show a playful drag ghost card with product image, name and price. */
export function setPlayfulProductDragImage(
  dataTransfer: DataTransfer | null,
  item: FlyItem,
  sourceImg?: HTMLImageElement | null,
) {
  if (typeof document === "undefined" || !dataTransfer) return;
  const ghost = createPlayfulDragGhost(item.imageUrl, item.name, item.price, sourceImg);
  document.body.appendChild(ghost);
  // Center the ghost on the cursor for a natural grab feel.
  dataTransfer.setDragImage(ghost, 74, 74);
  window.setTimeout(() => ghost.remove(), 0);
}

/** Επέλεξε τον ορατό στόχο καλαθιού: στο κινητό το πλωτό κουμπί κάτω δεξιά,
 * στον υπολογιστή το πλαϊνό καλάθι. Τα στοιχεία που είναι κρυμμένα με
 * `display:none` επιστρέφουν 0 client rects, οπότε τα προσπερνάμε. */
function resolveCartTarget(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const mobile = document.getElementById(CART_DROP_TARGET_MOBILE_ID);
  if (mobile && mobile.getClientRects().length > 0) return mobile;
  const desktop = document.getElementById(CART_DROP_TARGET_ID);
  if (desktop && desktop.getClientRects().length > 0) return desktop;
  return mobile ?? desktop;
}

function makeConfettiPiece(x: number, y: number, delay: number, hue: number): HTMLElement {
  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  el.style.width = "8px";
  el.style.height = "12px";
  el.style.borderRadius = "2px";
  el.style.backgroundColor = `hsl(${hue} 85% 55%)`;
  el.style.zIndex = "9999";
  el.style.pointerEvents = "none";
  el.style.willChange = "transform, opacity";
  const angle = (Math.random() - 0.5) * 2.4; // radians spread
  const dist = 40 + Math.random() * 80;
  const tx = Math.sin(angle) * dist;
  const ty = -60 - Math.random() * 90 + Math.cos(angle) * 30;
  const rot = (Math.random() - 0.5) * 720;
  el.animate(
    [
      { transform: "translate(0,0) rotate(0deg)", opacity: 1 },
      { transform: `translate(${tx}px, ${ty}px) rotate(${rot}deg)`, opacity: 0 },
    ],
    {
      duration: 700 + Math.random() * 300,
      delay,
      easing: "cubic-bezier(0.34, 0.64, 0.64, 1)",
      fill: "forwards",
    },
  );
  return el;
}

/** Throw a tiny confetti burst from the cart target to celebrate a drop. */
function celebrateDrop(target?: HTMLElement | null) {
  if (typeof document === "undefined") return;
  const rect = target?.getBoundingClientRect();
  const originX = rect
    ? rect.left + rect.width / 2
    : window.innerWidth - 80;
  const originY = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;

  const pieces: HTMLElement[] = [];
  for (let i = 0; i < 18; i++) {
    const hue = [30, 45, 140, 210, 260, 340][i % 6];
    const piece = makeConfettiPiece(originX, originY, i * 25, hue);
    document.body.appendChild(piece);
    pieces.push(piece);
  }

  window.setTimeout(() => pieces.forEach((p) => p.remove()), 1200);
}

/** Animate an image flying from a source rect to the cart target. */
export function flyToCart(opts: {
  fromRect: DOMRect;
  imageUrl: string | null;
  onArrive?: () => void;
}) {
  if (typeof window === "undefined") return;
  const target = resolveCartTarget();
  const targetRect = target?.getBoundingClientRect();
  if (!targetRect) {
    opts.onArrive?.();
    return;
  }

  const ghost = document.createElement("div");
  ghost.style.position = "fixed";
  ghost.style.left = `${opts.fromRect.left}px`;
  ghost.style.top = `${opts.fromRect.top}px`;
  ghost.style.width = `${opts.fromRect.width}px`;
  ghost.style.height = `${opts.fromRect.height}px`;
  ghost.style.zIndex = "9999";
  ghost.style.pointerEvents = "none";
  ghost.style.borderRadius = "8px";
  ghost.style.overflow = "hidden";
  ghost.style.background = "white";
  ghost.style.boxShadow = "0 8px 24px rgba(0,0,0,0.18)";
  ghost.style.transition =
    "transform 700ms cubic-bezier(0.4, 0, 0.2, 1), opacity 700ms ease, width 700ms ease, height 700ms ease";

  if (opts.imageUrl) {
    const img = document.createElement("img");
    img.src = opts.imageUrl;
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.objectFit = "contain";
    img.style.padding = "6px";
    ghost.appendChild(img);
  } else {
    ghost.style.display = "grid";
    ghost.style.placeItems = "center";
    ghost.style.fontSize = "32px";
    ghost.textContent = "🐼";
  }

  document.body.appendChild(ghost);

  const dx =
    targetRect.left + targetRect.width / 2 - (opts.fromRect.left + opts.fromRect.width / 2);
  const dy =
    targetRect.top + targetRect.height / 2 - (opts.fromRect.top + opts.fromRect.height / 2);

  requestAnimationFrame(() => {
    ghost.style.transform = `translate(${dx}px, ${dy}px) scale(0.15) rotate(8deg)`;
    ghost.style.opacity = "0.4";
  });

  window.setTimeout(() => {
    ghost.remove();
    if (target) {
      target.classList.add("animate-bounce-once");
      window.setTimeout(() => target.classList.remove("animate-bounce-once"), 500);
    }
    celebrateDrop(target);
    opts.onArrive?.();
  }, 720);
}

export function celebrateCartDrop() {
  celebrateDrop(resolveCartTarget());
}

/* ============================================================
 * Touch drag (κινητά): το HTML5 drag & drop δεν υποστηρίζεται σε
 * touch συσκευές, οπότε το υλοποιούμε χειροκίνητα με touch events.
 * ============================================================ */

export const CART_TOUCH_DRAG_EVENT = "mp-cart-touchdrag";

function emitTouchDragState(active: boolean, over: boolean) {
  window.dispatchEvent(
    new CustomEvent(CART_TOUCH_DRAG_EVENT, { detail: { active, over } }),
  );
}

function isOverTarget(
  x: number,
  y: number,
  target: HTMLElement | null,
  ghost?: HTMLElement | null,
): boolean {
  if (!target) return false;
  const r = target.getBoundingClientRect();
  const pad = 48; // πιο εύκολο «σημάδι» με το δάχτυλο σε μικρές οθόνες
  const fingerHits =
    x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad
  if (fingerHits) return true;

  // Το προϊόν προβάλλεται λίγο ψηλότερα από το δάχτυλο, ώστε να παραμένει
  // ορατό. Θεωρούμε επιτυχημένη και την πραγματική επικάλυψη της κάρτας με
  // το καλάθι, αντί να απαιτούμε το δάχτυλο να βρίσκεται ακριβώς στο κέντρο.
  if (ghost) {
    const g = ghost.getBoundingClientRect();
    return g.right >= r.left && g.left <= r.right && g.bottom >= r.top && g.top <= r.bottom;
  }
  return false;
}

/** Βρίσκει το πλαϊνό (ή πλωτό) καλάθι ως ολόκληρο container, όχι μόνο την κεφαλίδα. */
function resolveCartContainer(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;
  const sidebar = el.closest("[data-mp-cart-sidebar]") as HTMLElement | null;
  return sidebar ?? el;
}

/** Ελέγχει αν ένα ορθογώνιο είναι ορατό μέσα στο viewport. */
function isIntersectingViewport(r: DOMRect): boolean {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return r.width > 0 && r.height > 0 && r.left < vw && r.top < vh && r.right > 0 && r.bottom > 0;
}

/** Σκοτεινό overlay + οδηγία «Αφήστε το εδώ» με βελάκι προς το καλάθι. */
function createDropOverlay(target: HTMLElement | null) {
  const overlay = document.createElement("div");
  overlay.setAttribute("data-mp-drop-overlay", "");
  overlay.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:9990",
    "background:rgba(15,23,42,0.45)",
    "backdrop-filter:blur(1.5px)",
    "pointer-events:none",
    "opacity:0",
    "transition:opacity .18s ease, left .1s linear, right .1s linear, top .1s linear, bottom .1s linear",
  ].join(";");

  const coarse =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(pointer: coarse)").matches || window.innerWidth < 640);

  const hint = document.createElement("div");
  hint.style.cssText = [
    "position:absolute",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "gap:12px",
    "transform:translate(-50%,-50%)",
    "text-align:center",
    "max-width:min(88vw,420px)",
    "animation:mp-hint-pop .28s cubic-bezier(.34,1.56,.64,1) both",
  ].join(";");

  // Παιχνιδιάρικη «κάρτα» με panda, παλλόμενο δαχτυλίδι και μήνυμα.
  const card = document.createElement("div");
  card.style.cssText = [
    "position:relative",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "gap:10px",
    "padding:" + (coarse ? "16px 20px" : "22px 28px"),
    "border-radius:26px",
    "border:2px dashed rgba(255,255,255,.55)",
    "background:linear-gradient(135deg, rgba(249,115,22,.92), rgba(236,72,153,.88) 45%, rgba(99,102,241,.9))",
    "background-size:200% 200%",
    "animation:mp-hint-sheen 3s linear infinite",
    "box-shadow:0 22px 60px -18px rgba(0,0,0,.65)",
    "backdrop-filter:blur(6px)",
  ].join(";");

  const panda = document.createElement("div");
  panda.textContent = "🐼";
  panda.style.cssText = [
    "font-size:" + (coarse ? "34px" : "44px"),
    "line-height:1",
    "animation:mp-hint-float 1.6s ease-in-out infinite",
    "filter:drop-shadow(0 6px 10px rgba(0,0,0,.35))",
  ].join(";");

  const label = document.createElement("div");
  label.textContent = "Σύρε και άφησε το προϊόν στο καλάθι για προσθήκη";
  label.style.cssText = [
    "font-weight:800",
    "font-size:" + (coarse ? "15px" : "20px"),
    "line-height:1.3",
    "color:#fff",
    "white-space:normal",
    "text-shadow:0 2px 10px rgba(0,0,0,.45)",
  ].join(";");

  const arrowWrap = document.createElement("div");
  arrowWrap.style.cssText = "position:relative;display:flex;align-items:center;justify-content:center";

  const ring = document.createElement("div");
  ring.style.cssText = [
    "position:absolute",
    "width:" + (coarse ? "46px" : "58px"),
    "height:" + (coarse ? "46px" : "58px"),
    "border-radius:9999px",
    "border:2px solid rgba(255,255,255,.7)",
    "animation:mp-hint-ring 1.2s ease-out infinite",
  ].join(";");

  const arrow = document.createElement("div");
  arrow.textContent = "↘";
  arrow.style.cssText = [
    "font-size:" + (coarse ? "34px" : "46px"),
    "line-height:1",
    "color:#fff",
    "text-shadow:0 4px 14px rgba(0,0,0,.5)",
    "animation:mp-drop-arrow-diag .9s ease-in-out infinite",
  ].join(";");

  arrowWrap.appendChild(ring);
  arrowWrap.appendChild(arrow);
  card.appendChild(panda);
  card.appendChild(label);
  hint.appendChild(card);
  hint.appendChild(arrowWrap);
  overlay.appendChild(hint);
  document.body.appendChild(overlay);


  // Το καλάθι πρέπει να «λάμπει» πάνω από το σκοτεινό overlay: υψώνουμε το
  // z-index του container και περιορίζουμε το overlay ώστε να μην καλύπτει το
  // πλαϊνό καλάθι.
  const rawTarget = target ?? resolveCartTarget();
  const cartContainer = resolveCartContainer(rawTarget);
  const prevZ = cartContainer?.style.zIndex ?? "";
  const prevPos = cartContainer?.style.position ?? "";
  if (cartContainer) {
    if (getComputedStyle(cartContainer).position === "static") cartContainer.style.position = "relative";
    cartContainer.style.zIndex = "9995";
  }

  const clearExclusion = () => {
    overlay.style.left = "0";
    overlay.style.right = "0";
    overlay.style.top = "0";
    overlay.style.bottom = "0";
  };

  const updateExclusion = () => {
    if (!cartContainer || !cartContainer.isConnected) {
      clearExclusion();
      return;
    }
    const r = cartContainer.getBoundingClientRect();
    if (!isIntersectingViewport(r)) {
      clearExclusion();
      return;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const leftEdge = Math.round(r.left);
    const rightEdge = Math.round(vw - r.right);
    const topEdge = Math.round(r.top);
    const bottomEdge = Math.round(vh - r.bottom);

    // Αν το καλάθι ακουμπάει στο δεξί άκρο και είναι ψηλό, το εξαιρούμε από
    // τα δεξιά. Αν ακουμπάει αριστερά/πάνω/κάτω, προσαρμόζουμε αντίστοιχα.
    // Προεπιλογή: εξαίρεση από τη δεξιά πλευρά.
    if (r.right >= vw - 8 && r.height >= vh * 0.6) {
      overlay.style.left = "0";
      overlay.style.right = `${Math.max(0, rightEdge)}px`;
      overlay.style.top = "0";
      overlay.style.bottom = "0";
    } else if (r.left <= 8 && r.height >= vh * 0.6) {
      overlay.style.left = `${Math.max(0, leftEdge + r.width)}px`;
      overlay.style.right = "0";
      overlay.style.top = "0";
      overlay.style.bottom = "0";
    } else if (r.bottom >= vh - 8 && r.width >= vw * 0.6) {
      overlay.style.left = "0";
      overlay.style.right = "0";
      overlay.style.top = "0";
      overlay.style.bottom = `${Math.max(0, bottomEdge)}px`;
    } else if (r.top <= 8 && r.width >= vw * 0.6) {
      overlay.style.left = "0";
      overlay.style.right = "0";
      overlay.style.top = `${Math.max(0, topEdge + r.height)}px`;
      overlay.style.bottom = "0";
    } else {
      // Φορητό/μικρό κουμπί: μην εξαιρείς τίποτα.
      clearExclusion();
    }
  };

  const place = () => {
    // Πάντα κεντραρισμένη οδηγία (κινητά + υπολογιστές) ώστε να μην κόβεται.
    hint.style.left = "50%";
    hint.style.top = "50%";

    // Εξαιρούμε πάντα το ορατό καλάθι από το σκοτείνιασμα.
    const currentTarget = target ?? resolveCartTarget();
    const currentContainer = resolveCartContainer(currentTarget);
    if (currentContainer?.getClientRects().length && isIntersectingViewport(currentContainer.getBoundingClientRect())) {
      updateExclusion();
    } else {
      clearExclusion();
    }
  };
  place();
  requestAnimationFrame(() => {
    overlay.style.opacity = "1";
  });

  return {
    reposition: place,
    remove() {
      overlay.style.opacity = "0";
      window.setTimeout(() => overlay.remove(), 180);
      if (cartContainer) {
        cartContainer.style.zIndex = prevZ;
        cartContainer.style.position = prevPos;
      }
    },
  };
}

let activeHint: { reposition: () => void; remove: () => void } | null = null;
let hintRaf = 0;

/** Εμφανίζει το σκοτείνιασμα + οδηγία σε κάθε συσκευή (και desktop drag). */
export function showCartDropHint() {
  if (typeof document === "undefined" || activeHint) return;
  activeHint = createDropOverlay(resolveCartTarget());
  const loop = () => {
    if (!activeHint) return;
    activeHint.reposition();
    hintRaf = requestAnimationFrame(loop);
  };
  hintRaf = requestAnimationFrame(loop);
}

/** Κρύβει την οδηγία μεταφοράς. */
export function hideCartDropHint() {
  if (hintRaf) cancelAnimationFrame(hintRaf);
  hintRaf = 0;
  activeHint?.remove();
  activeHint = null;
}

/**
 * Ξεκινά drag με σύντομο κράτημα ή αμέσως μόλις το δάχτυλο κινηθεί
 * οριζόντια προς το καλάθι. Η καθαρά κάθετη κίνηση παραμένει scroll.
 */
export function beginTouchDrag(opts: {
  touch: { clientX: number; clientY: number };
  item: FlyItem;
  sourceImg?: HTMLImageElement | null;
  onDragStateChange?: (dragging: boolean) => void;
  onDrop: () => void;
}) {
  if (typeof document === "undefined") return;

  const startX = opts.touch.clientX;
  const startY = opts.touch.clientY;
  let ghost: HTMLElement | null = null;
  let dragging = false;
  let over = false;
  let lastX = startX;
  let lastY = startY;
  // Το κράτημα μόνο «οπλίζει» το drag. Χρειάζεται και κίνηση του δαχτύλου για
  // να ξεκινήσει, ώστε ένα απλό tap να ανοίγει πάντα τη σελίδα προϊόντος.
  let armed = false;
  let holdTimer = window.setTimeout(() => {
    armed = true;
    if (navigator.vibrate) navigator.vibrate(8);
  }, 320);
  let overlay: { reposition: () => void; remove: () => void } | null = null;

  function startDragging() {
    if (dragging) return;
    dragging = true;
    overlay = createDropOverlay(resolveCartTarget());
    ghost = createPlayfulDragGhost(
      opts.item.imageUrl,
      opts.item.name,
      opts.item.price,
      opts.sourceImg,
    );
    ghost.style.left = "0px";
    ghost.style.top = "0px";
    ghost.style.zIndex = "9999";
    ghost.style.opacity = "0.95";
    ghost.style.transform = `translate(${startX - 74}px, ${startY - 90}px) scale(1.05)`;
    document.body.appendChild(ghost);
    document.body.style.overflow = "hidden";
    opts.onDragStateChange?.(true);
    emitTouchDragState(true, false);
    if (navigator.vibrate) navigator.vibrate(12);
  }

  const move = (e: TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    lastX = t.clientX;
    lastY = t.clientY;
    if (!dragging) {
      const dx = Math.abs(t.clientX - startX);
      const dy = Math.abs(t.clientY - startY);
      // Η οριζόντια/διαγώνια κίνηση προς το πλωτό καλάθι ξεκινά άμεσα το
      // drag. Μια σαφώς κάθετη χειρονομία παραμένει φυσικό scroll.
      if ((armed && (dx > 6 || dy > 6)) || (dx > 8 && dx >= dy * 0.65)) {
        window.clearTimeout(holdTimer);
        startDragging();
      } else if (dy > 14) {
        cleanup();
        return;
      } else {
        return;
      }
    }
    e.preventDefault();
    if (ghost) {
      ghost.style.transform = `translate(${t.clientX - 74}px, ${t.clientY - 90}px) scale(1.05)`;
    }
    overlay?.reposition();
    const target = resolveCartTarget();
    const nowOver = isOverTarget(t.clientX, t.clientY, target, ghost);
    if (nowOver !== over) {
      over = nowOver;
      emitTouchDragState(true, over);
      if (over && navigator.vibrate) navigator.vibrate(8);
    }
  };

  const end = (e: TouchEvent) => {
    const t = e.changedTouches[0];
    const endX = t?.clientX ?? lastX;
    const endY = t?.clientY ?? lastY;
    const target = resolveCartTarget();
    const dropped =
      dragging && (over || isOverTarget(endX, endY, target, ghost));
    if (dropped) {
      opts.onDrop();
      celebrateDrop(target);
      if (target) {
        target.classList.add("animate-bounce-once");
        window.setTimeout(() => target.classList.remove("animate-bounce-once"), 500);
      }
      if (navigator.vibrate) navigator.vibrate([10, 30, 10]);
    }
    cleanup();
  };

  function cleanup() {
    window.clearTimeout(holdTimer);
    ghost?.remove();
    ghost = null;
    overlay?.remove();
    overlay = null;
    if (dragging) {
      document.body.style.overflow = "";
      opts.onDragStateChange?.(false);
      emitTouchDragState(false, false);
    }
    dragging = false;
    document.removeEventListener("touchmove", move);
    document.removeEventListener("touchend", end);
    document.removeEventListener("touchcancel", end);
  }

  document.addEventListener("touchmove", move, { passive: false });
  document.addEventListener("touchend", end);
  document.addEventListener("touchcancel", end);
}
